import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../../i18n/useTranslation';
import { licensesManageService } from '../../../../api/licensesManageService';
import type { ManageLicenseListDto } from '../../../../types/manageLicenses';

interface Props {
  open: boolean;
  license: ManageLicenseListDto;
  activeSessionCount: number;
  onClose: () => void;
  onCompleted: (license: ManageLicenseListDto) => void;
}

export default function LicenseSuspendAndTerminateDialog({
  open,
  license,
  activeSessionCount,
  onClose,
  onCompleted,
}: Props) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [isPending, setIsPending] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (!open) return;
    setReason('');
  }, [open]);

  if (!open) return null;

  const canSubmit = reason.trim().length > 0 && !isPending;

  const submit = async () => {
    setIsPending(true);
    try {
      const updated = await licensesManageService.suspendAndTerminate(license.id, reason.trim());
      await qc.invalidateQueries({ queryKey: ['manage-licenses'] });
      onCompleted(updated);
      onClose();
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-5">
        <h2 className="text-lg font-semibold mb-2 text-red-700">
          {t('App.Admin.Licenses.LicenseSuspendAndTerminateDialog.Title')}
        </h2>
        <p className="text-sm text-neutral-700 mb-4">
          {t('App.Admin.Licenses.LicenseSuspendAndTerminateDialog.Description', { count: activeSessionCount, email: license.studentEmail, bookTitle: license.bookTitle })}
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            {t('App.Admin.Licenses.LicenseSuspendAndTerminateDialog.ReasonLabel')} <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
            rows={3}
            maxLength={1000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('App.Admin.Licenses.LicenseSuspendAndTerminateDialog.ReasonPlaceholder')}
          />
          <p className="text-xs text-neutral-500 text-right mt-0.5">{t('App.Admin.Licenses.LicenseSuspendAndTerminateDialog.CharCount', { count: reason.length })}</p>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-100"
          >
            {t('App.Admin.Licenses.LicenseSuspendAndTerminateDialog.Cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            {isPending ? t('App.Admin.Licenses.LicenseSuspendAndTerminateDialog.Suspending') : t('App.Admin.Licenses.LicenseSuspendAndTerminateDialog.SuspendAndTerminate')}
          </button>
        </div>
      </div>
    </div>
  );
}
